import React, { useState } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import {
  Toolbar,
  Tooltip,
  IconButton,
  Typography,
  OutlinedInput,
  InputAdornment,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  ListItemText,
  Box,
} from "@mui/material";
import Iconify from "../../../components/Iconify";

// ----------------------------------------------------------------------

const RootStyle = styled(Toolbar)(({ theme }) => ({
  height: 96,
  display: "flex",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1, 0, 3),
}));

const SearchStyle = styled(OutlinedInput)(({ theme }) => ({
  width: 240,
  transition: theme.transitions.create(["box-shadow", "width"], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  "&.Mui-focused": { width: 320, boxShadow: theme.customShadows.z8 },
  "& fieldset": {
    borderWidth: `1px !important`,
    borderColor: `${theme.palette.grey[500_32]} !important`,
  },
}));

// ----------------------------------------------------------------------

UserListToolbar.propTypes = {
  numSelected: PropTypes.number,
  filterName: PropTypes.string,
  onFilterName: PropTypes.func,
  onClearSearch: PropTypes.func, // Optionally handle search clearing
  onApplyFilters: PropTypes.func, // Function to handle applying filters
};

export default function UserListToolbar({
  numSelected,
  filterName,
  onFilterName,
  onClearSearch,
  onApplyFilters,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMembership, setActiveMembership] = useState(false);
  const [selectedMembershipType, setSelectedMembershipType] = useState([]);
  const [selectedSportType, setSelectedSportType] = useState([]);
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState([]);

  const membershipTypes = [
    "Adult Seasonal 24/7 Squash/Racquetball/Gym",
    "Junior Annual",
    "Annual Membership",
  ];
  const sportTypes = ["Squash Player", "Racquetball Player"];
  const invoiceStatuses = ["PAID", "PENDING", "CANCELED"];

  const handleFilterMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setAnchorEl(null);
    onApplyFilters({
      activeMembership,
      selectedMembershipType,
      selectedSportType,
      selectedInvoiceStatus,
    });
  };

  const handleMembershipTypeChange = (event) => {
    const { value } = event.target;
    setSelectedMembershipType((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  };

  const handleSportTypeChange = (event) => {
    const { value } = event.target;
    setSelectedSportType((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  };

  const handleInvoiceStatusChange = (event) => {
    const { value } = event.target;
    setSelectedInvoiceStatus((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  };

  const handleActiveMembershipChange = (event) => {
    const { checked } = event.target;
    setActiveMembership(checked);
  };

  const handleClearSearch = () => {
    onFilterName({ target: { value: "" } }); // Reset the filterName state
  };

  return (
    <RootStyle
      sx={{
        ...(numSelected > 0 && {
          color: "primary.main",
          bgcolor: "primary.lighter",
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography component="div" variant="subtitle1">
          {numSelected} selected
        </Typography>
      ) : (
        <SearchStyle
          value={filterName}
          onChange={onFilterName}
          placeholder="Search member..."
          startAdornment={
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: "text.disabled" }} />
            </InputAdornment>
          }
          endAdornment={
            filterName ? (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch}>
                  <Iconify icon="eva:close-fill" />
                </IconButton>
              </InputAdornment>
            ) : null
          }
        />
      )}

      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton>
            <Iconify icon="eva:trash-2-fill" />
          </IconButton>
        </Tooltip>
      ) : (
        <>
          <Tooltip title="Filter list">
            <IconButton onClick={handleFilterMenuClick}>
              <Iconify icon="ic:round-filter-list" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleFilterMenuClose}
          >
            <Box sx={{ padding: 2 }}>
              <MenuItem>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={activeMembership}
                      onChange={handleActiveMembershipChange}
                    />
                  }
                  label="Active Membership"
                />
              </MenuItem>

              <Box sx={{ paddingTop: 1 }}>
                <ListItemText>Membership Type</ListItemText>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {membershipTypes.map((type) => (
                    <FormControlLabel
                      key={type}
                      control={
                        <Checkbox
                          checked={selectedMembershipType.includes(type)}
                          onChange={handleMembershipTypeChange}
                          value={type}
                        />
                      }
                      label={type}
                    />
                  ))}
                </Box>
              </Box>

              <Box sx={{ paddingTop: 1 }}>
                <ListItemText>Sport</ListItemText>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {sportTypes.map((type) => (
                    <FormControlLabel
                      key={type}
                      control={
                        <Checkbox
                          checked={selectedSportType.includes(type)}
                          onChange={handleSportTypeChange}
                          value={type}
                        />
                      }
                      label={type}
                    />
                  ))}
                </Box>
              </Box>

              <Box sx={{ paddingTop: 1 }}>
                <ListItemText>Invoice Status</ListItemText>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {invoiceStatuses.map((status) => (
                    <FormControlLabel
                      key={status}
                      control={
                        <Checkbox
                          checked={selectedInvoiceStatus.includes(status)}
                          onChange={handleInvoiceStatusChange}
                          value={status}
                        />
                      }
                      label={status}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Menu>
        </>
      )}
    </RootStyle>
  );
}
